/**
 * TileMeta — Tile Intelligence System
 *
 * Every tileset in TileForge gets a TileMeta companion that teaches the
 * generator how to use each tile correctly. This solves three problems:
 *
 * 1. TERRAIN TRANSITIONS — water edges, road curves, grass-to-dirt blending.
 *    Tiles belong to terrain groups; each group defines its autotile variants
 *    (16-tile cardinal or 47-tile blob) and which terrains it transitions to.
 *
 * 2. MULTI-TILE OBJECTS — beds, cars, tables that span multiple tiles.
 *    Each object defines its tile grid, footprint, collision, placement rules
 *    (which rooms, wall alignment, clearance), and clipping rules.
 *
 * 3. TILE ROLES — what zone/purpose each tile serves.
 *    Maps tile IDs back to semantic zones so generators can work abstractly.
 *
 * The schema is designed for two workflows:
 *   A) BUNDLED TILESETS — metadata hand-authored by us, shipped with TileForge
 *   B) USER-IMPORTED TILESETS — user tags tiles via the Tile Tagger UI,
 *      TileMeta is built from those tags and saved alongside the tileset
 */

import { ZONE } from './ZoneTypes.js';
import { AutotileResolver } from './AutotileResolver.js';
import { ZoneMapper } from './ZoneMapper.js';

export const SCHEMA_VERSION = 3;

// ─── Terrain Autotile Formats ────────────────────────────────────────

/**
 * 4-bit cardinal bitmask: N=1, E=2, S=4, W=8 → 16 variants.
 * Simple, works for basic terrain edges.
 */
export const AUTOTILE_4BIT = '4bit';

/**
 * 8-bit blob bitmask (Wang/blob tileset): includes diagonals → 47 unique shapes.
 * The standard for professional pixel art terrain sets.
 *
 * Bit layout:
 *   NW=1, N=2, NE=4, W=8, E=16, SW=32, S=64, SE=128
 *
 * Only 47 of 256 combinations produce unique shapes because diagonal bits
 * only matter when both adjacent cardinal neighbors match.
 */
export const AUTOTILE_47 = '47blob';

/**
 * Road network: straights, curves, T-junctions, crossroads, dead-ends.
 * Uses same 4-bit cardinal mask but maps to road-specific shapes.
 */
export const AUTOTILE_ROAD = 'road';

/**
 * No autotiling — single tile, no neighbor awareness.
 */
export const AUTOTILE_NONE = 'none';


// ─── Layer Constants ─────────────────────────────────────────────────

export const LAYER = {
  GROUND:   'ground',
  OBJECTS:  'objects',
  OVERHEAD: 'overhead',
};

/** Default layer for each role — used when no explicit layer is tagged. */
export const ROLE_DEFAULT_LAYER = {
  terrain:    LAYER.GROUND,
  wall:       LAYER.OBJECTS,
  floor:      LAYER.GROUND,
  transition: LAYER.GROUND,
  opening:    LAYER.OBJECTS,
  decoration: LAYER.OBJECTS,
  object:     LAYER.OBJECTS,
  overlay:    LAYER.OVERHEAD,
  anim_frame: LAYER.OBJECTS,
};

// ─── Tile Role Categories ────────────────────────────────────────────

export const ROLE = {
  // Terrain (ground layer, autotiled)
  TERRAIN:     'terrain',
  // Wall (structure layer)
  WALL:        'wall',
  // Floor (interior ground layer)
  FLOOR:       'floor',
  // Transition (edge tiles between terrains — auto-assigned, not tagged directly)
  TRANSITION:  'transition',
  // Door / window (structure openings)
  OPENING:     'opening',
  // Single-tile decoration (non-blocking or blocking)
  DECORATION:  'decoration',
  // Multi-tile object (furniture, vehicles, structures)
  OBJECT:      'object',
  // Overlay (tiles drawn on top of others — shadows, cracks, moss)
  OVERLAY:     'overlay',
  // Animation frame (not placed directly — part of an animation sequence)
  ANIM_FRAME:  'anim_frame',
};


// ─── Schema Definitions ─────────────────────────────────────────────

/**
 * @typedef {Object} TerrainGroup
 * @property {string} id             — Unique name: 'grass', 'water', 'road', 'dirt'
 * @property {string} autotileFormat — AUTOTILE_4BIT | AUTOTILE_47 | AUTOTILE_ROAD | AUTOTILE_NONE
 * @property {number} centerTile     — Tile ID for the fully-surrounded center variant
 * @property {Object<number,number>} variants — Bitmask → tile ID mapping
 *   For 4-bit: keys 0–15. For 47-blob: keys are the 47 canonical bitmask values.
 *   For road: keys are named shapes (see ROAD_SHAPES).
 * @property {string[]} transitionsTo — IDs of terrain groups this can border
 *   (controls which transition tiles get used at boundaries)
 * @property {number[]} [randomVariants] — Additional center-tile variants for visual variety
 * @property {boolean} [animated]    — Whether center tile has animation frames
 * @property {number[]} [animFrames] — Frame tile IDs for animation
 * @property {number} [animSpeed]    — Ms per frame
 * @property {string} [layer]        — Target TMX layer: 'ground' | 'objects' | 'overhead' (default: 'ground')
 */

/**
 * @typedef {Object} WallSet
 * @property {string} id             — 'exterior', 'interior', 'stone', 'wood'
 * @property {number} top            — Tile ID for wall facing north (seen from above)
 * @property {number} bottom         — Wall facing south
 * @property {number} left           — Wall facing west
 * @property {number} right          — Wall facing east
 * @property {number} cornerTL       — Top-left outer corner
 * @property {number} cornerTR       — Top-right outer corner
 * @property {number} cornerBL       — Bottom-left outer corner
 * @property {number} cornerBR       — Bottom-right outer corner
 * @property {number} [innerTL]      — Top-left inner corner (concave)
 * @property {number} [innerTR]
 * @property {number} [innerBL]
 * @property {number} [innerBR]
 * @property {number} [teeN]         — T-junction facing north
 * @property {number} [teeE]
 * @property {number} [teeS]
 * @property {number} [teeW]
 * @property {number} [cross]        — 4-way intersection
 * @property {number} [endN]         — Dead-end cap facing north
 * @property {number} [endE]
 * @property {number} [endS]
 * @property {number} [endW]
 * @property {number} [single]       — Isolated single wall tile
 * @property {string} [layer]        — Target TMX layer (default: 'objects')
 */

/**
 * @typedef {Object} FloorType
 * @property {string} id             — 'wood', 'tile_white', 'carpet_red', 'concrete'
 * @property {number} tile           — Primary tile ID
 * @property {number[]} [variants]   — Random variant tile IDs
 * @property {string[]} [zones]      — Which zones use this floor: ['KITCHEN', 'BATHROOM']
 * @property {string} [layer]        — Target TMX layer (default: 'ground')
 */

/**
 * @typedef {Object} TileObject
 * @property {string} id             — 'double_bed', 'sedan', 'dining_table'
 * @property {number[][]} tiles      — 2D array of tile IDs [row][col], 0 = empty cell
 * @property {Object} footprint      — { w: number, h: number }
 * @property {boolean|boolean[][]} blocking  — true = all cells block, or per-cell grid
 * @property {Object} placement      — Placement rules:
 * @property {string[]} placement.zones     — Valid zones: ['BEDROOM', 'MASTER']
 * @property {string} [placement.wallAlign] — 'north'|'south'|'east'|'west'|'any'|'corner'
 * @property {Object} [placement.clearance] — { north:1, south:2 } — tiles of free space needed
 * @property {number} [placement.minRoom]   — Minimum room size (tiles) to place this object
 * @property {Object|boolean} [clippable]   — false, or { north:bool, south:bool, east:bool, west:bool }
 *   When true for a direction, the object can be partially placed with tiles beyond the
 *   boundary omitted (e.g., a car at the map edge loses its bumper).
 * @property {number} [weight]       — Selection weight for random placement (default 1)
 * @property {string} [layer]        — Target TMX layer (default: 'objects')
 */

/**
 * @typedef {Object} SingleDecoration
 * @property {string} id             — 'crate', 'flower_red', 'barrel'
 * @property {number} tile           — Tile ID
 * @property {boolean} blocking      — Whether it blocks movement
 * @property {string[]} zones        — Valid placement zones
 * @property {boolean} [indoor]      — Can appear indoors
 * @property {boolean} [outdoor]     — Can appear outdoors
 * @property {boolean} [scatter]     — Use Poisson scatter (vs deliberate placement)
 * @property {number} [weight]       — Selection weight
 * @property {string} [layer]        — Target TMX layer (default: 'objects')
 */

/**
 * @typedef {Object} OpeningDef
 * @property {string} id             — 'wooden_door', 'glass_window', 'archway'
 * @property {string} type           — 'door' | 'window' | 'archway'
 * @property {number} tile           — Tile ID
 * @property {number} [tileOpen]     — Open-state tile (for doors)
 * @property {number} [tileBroken]   — Broken-state tile (for windows)
 * @property {string} [orientation]  — 'horizontal' | 'vertical' | 'any'
 * @property {string} [layer]        — Target TMX layer (default: 'objects')
 */


// ─── TileMeta Class ─────────────────────────────────────────────────

export class TileMeta {
  /**
   * @param {Object} data — Raw metadata object (loaded from JSON or built by tagger)
   * @param {string} data.id            — Tileset identifier
   * @param {string} data.name          — Human-readable name
   * @param {number} data.tileSize      — Tile pixel size (16, 32, etc.)
   * @param {number} data.columns       — Tileset columns
   * @param {number} data.tileCount     — Total tile count in the tileset
   * @param {TerrainGroup[]} data.terrainGroups
   * @param {WallSet[]} data.wallSets
   * @param {FloorType[]} data.floorTypes
   * @param {TileObject[]} data.objects
   * @param {SingleDecoration[]} data.decorations
   * @param {OpeningDef[]} data.openings
   * @param {Object<number,string>} [data.tileRoles] — Per-tile role overrides: { tileId: ROLE }
   * @param {Object<number,string[]>} [data.tileTags] — Free-form tags per tile: { tileId: ['grass','edge','north'] }
   * @param {Object} [data.attribution] — Tileset source and licensing info
   * @param {string} [data.attribution.artist]      — Artist/studio name
   * @param {string} [data.attribution.purchaseUrl]  — Where to buy/download the tileset
   * @param {string} [data.attribution.licenseType]  — 'commercial', 'cc-by', 'free', etc.
   * @param {string} [data.attribution.licenseUrl]   — Link to full license terms
   * @param {string} [data.attribution.website]      — Artist's homepage
   * @param {string} [data.attribution.notes]        — Any additional license notes
   */
  constructor(data) {
    this.schemaVersion = data.schemaVersion || 1;
    this.id = data.id;
    this.name = data.name || data.id;
    this.tileSize = data.tileSize || 16;
    this.columns = data.columns || 1;
    this.tileCount = data.tileCount || 0;

    // Attribution and licensing
    this.attribution = data.attribution || null;

    // Indexed lookups
    this._terrainGroups = new Map();
    this._wallSets = new Map();
    this._floorTypes = new Map();
    this._objects = new Map();
    this._decorations = new Map();
    this._openings = new Map();

    // Per-tile metadata
    this._tileRoles = new Map();   // tileId → ROLE
    this._tileLayers = new Map();  // tileId → LAYER (explicit overrides from tagger)
    this._tileTags = new Map();    // tileId → Set<string>
    this._tileToTerrain = new Map(); // tileId → terrainGroup id
    this._tileToObject = new Map();  // tileId → object id (for any tile in an object)
    this._tileHashes = new Map();    // tileId → CRC32 hex string

    // Ingestion warnings (tile conflicts, invalid data)
    this._warnings = [];

    this._ingest(data);

    // Composed subsystems (operate on our Maps by reference)
    this._resolver = new AutotileResolver(this._terrainGroups, this._wallSets);
    this._zoneMapper = new ZoneMapper(this);
  }

  // ─── Ingestion ──────────────────────────────────────────────────

  _ingest(data) {
    // Terrain groups
    for (const tg of (data.terrainGroups || [])) {
      this._terrainGroups.set(tg.id, tg);
      // Index all tiles in this terrain group back to the group
      if (tg.centerTile != null) this._indexTerrainTile(tg.centerTile, tg.id);
      for (const tileId of Object.values(tg.variants || {})) {
        this._indexTerrainTile(tileId, tg.id);
      }
      for (const tileId of (tg.randomVariants || [])) {
        this._indexTerrainTile(tileId, tg.id);
      }
    }

    // Wall sets
    for (const ws of (data.wallSets || [])) {
      this._wallSets.set(ws.id, ws);
      for (const tileId of Object.values(ws)) {
        if (typeof tileId === 'number') this._setRole(tileId, ROLE.WALL);
      }
    }

    // Floor types
    for (const ft of (data.floorTypes || [])) {
      this._floorTypes.set(ft.id, ft);
      this._setRole(ft.tile, ROLE.FLOOR);
      for (const v of (ft.variants || [])) this._setRole(v, ROLE.FLOOR);
    }

    // Multi-tile objects
    for (const obj of (data.objects || [])) {
      this._objects.set(obj.id, obj);
      if (obj.tiles) {
        for (const row of obj.tiles) {
          for (const tileId of row) {
            if (tileId != null && tileId >= 0) {
              this._setRole(tileId, ROLE.OBJECT);
              this._tileToObject.set(tileId, obj.id);
            }
          }
        }
      }
    }

    // Single decorations
    for (const dec of (data.decorations || [])) {
      this._decorations.set(dec.id, dec);
      this._setRole(dec.tile, ROLE.DECORATION);
    }

    // Openings
    for (const op of (data.openings || [])) {
      this._openings.set(op.id, op);
      this._setRole(op.tile, ROLE.OPENING);
      if (op.tileOpen != null) this._setRole(op.tileOpen, ROLE.OPENING);
      if (op.tileBroken != null) this._setRole(op.tileBroken, ROLE.OPENING);
    }

    // Explicit role overrides
    if (data.tileRoles) {
      for (const [id, role] of Object.entries(data.tileRoles)) {
        this._tileRoles.set(Number(id), role);
      }
    }

    // Free-form tags
    if (data.tileTags) {
      for (const [id, tags] of Object.entries(data.tileTags)) {
        this._tileTags.set(Number(id), new Set(tags));
      }
    }

    // Tile content hashes
    if (data.tileHashes) {
      for (const [id, hash] of Object.entries(data.tileHashes)) {
        this._tileHashes.set(Number(id), hash);
      }
    }

    // Direct tileLayers map (from serialized TileMeta)
    if (data.tileLayers) {
      for (const [id, layer] of Object.entries(data.tileLayers)) {
        this._tileLayers.set(Number(id), layer);
      }
    }

    // Explicit layer overrides from tileProperties (set by Tile Tagger)
    if (data.tileProperties) {
      for (const [id, props] of Object.entries(data.tileProperties)) {
        if (props.layer) this._tileLayers.set(Number(id), props.layer);
      }
    }

    // Layer overrides from schema structures (terrain groups, wall sets, etc.)
    this._ingestStructureLayers();
  }

  _indexTerrainTile(tileId, terrainId) {
    this._setRole(tileId, ROLE.TERRAIN, `terrain:${terrainId}`);
    this._tileToTerrain.set(tileId, terrainId);
  }

  _setRole(tileId, role, source = '') {
    if (tileId == null) return;
    const existing = this._tileRoles.get(tileId);
    if (existing && existing !== role) {
      this._warnings.push({
        type: 'role_conflict',
        tileId,
        existingRole: existing,
        newRole: role,
        source,
        message: `Tile ${tileId} already tagged as '${existing}', ignoring '${role}' from ${source}`,
      });
      return; // first-write-wins, but now we warn
    }
    this._tileRoles.set(tileId, role);
  }

  /**
   * Index explicit layer overrides from schema structures.
   * Only writes if the structure has an explicit `layer` field — doesn't
   * override per-tile tileProperties layers already ingested.
   */
  _ingestStructureLayers() {
    const setIfExplicit = (tileId, layer) => {
      if (tileId == null || !layer) return;
      if (!this._tileLayers.has(tileId)) this._tileLayers.set(tileId, layer);
    };

    for (const tg of this._terrainGroups.values()) {
      if (!tg.layer) continue;
      setIfExplicit(tg.centerTile, tg.layer);
      for (const id of Object.values(tg.variants || {})) setIfExplicit(id, tg.layer);
      for (const id of (tg.randomVariants || [])) setIfExplicit(id, tg.layer);
    }
    for (const ws of this._wallSets.values()) {
      if (!ws.layer) continue;
      for (const [k, v] of Object.entries(ws)) {
        if (typeof v === 'number') setIfExplicit(v, ws.layer);
      }
    }
    for (const ft of this._floorTypes.values()) {
      if (!ft.layer) continue;
      setIfExplicit(ft.tile, ft.layer);
      for (const v of (ft.variants || [])) setIfExplicit(v, ft.layer);
    }
    for (const obj of this._objects.values()) {
      if (!obj.layer) continue;
      for (const row of (obj.tiles || [])) {
        for (const id of row) setIfExplicit(id, obj.layer);
      }
    }
    for (const dec of this._decorations.values()) {
      if (!dec.layer) continue;
      setIfExplicit(dec.tile, dec.layer);
    }
    for (const op of this._openings.values()) {
      if (!op.layer) continue;
      setIfExplicit(op.tile, op.layer);
      setIfExplicit(op.tileOpen, op.layer);
      setIfExplicit(op.tileBroken, op.layer);
    }
  }

  // ─── Validation ─────────────────────────────────────────────────

  /**
   * Validate the loaded metadata. Returns array of errors/warnings.
   * Call after construction to check data integrity.
   */
  validate() {
    const errors = [];

    if (!this.id) errors.push({ level: 'error', message: 'Missing required field: id' });

    // Check tile IDs are in valid range
    const checkTileId = (id, context) => {
      if (typeof id !== 'number' || id < 0 || !Number.isInteger(id)) {
        errors.push({ level: 'error', message: `Invalid tile ID ${id} in ${context}` });
      } else if (this.tileCount > 0 && id >= this.tileCount) {
        errors.push({ level: 'warning', message: `Tile ID ${id} in ${context} exceeds tileCount (${this.tileCount})` });
      }
    };

    for (const [id, tg] of this._terrainGroups) {
      if (tg.centerTile != null) checkTileId(tg.centerTile, `terrain '${id}' centerTile`);
      for (const [mask, tileId] of Object.entries(tg.variants || {})) {
        checkTileId(Number(tileId), `terrain '${id}' variant mask=${mask}`);
      }
      for (const tileId of (tg.randomVariants || [])) {
        checkTileId(tileId, `terrain '${id}' randomVariant`);
      }
      // Terrain shouldn't transition to itself
      if (tg.transitionsTo?.includes(id)) {
        errors.push({ level: 'warning', message: `Terrain '${id}' lists itself in transitionsTo` });
      }
    }

    for (const [id, ws] of this._wallSets) {
      if (ws.top == null) errors.push({ level: 'warning', message: `Wall set '${id}' missing 'top' tile` });
      for (const [key, val] of Object.entries(ws)) {
        if (typeof val === 'number') checkTileId(val, `wallSet '${id}' ${key}`);
      }
    }

    for (const [id, ft] of this._floorTypes) {
      checkTileId(ft.tile, `floorType '${id}'`);
      for (const v of (ft.variants || [])) checkTileId(v, `floorType '${id}' variant`);
    }

    // Include ingestion warnings
    for (const w of this._warnings) {
      errors.push({ level: 'warning', message: w.message });
    }

    return errors;
  }

  /** Get ingestion warnings (tile conflicts, etc.) */
  getWarnings() { return [...this._warnings]; }

  // ─── Lookups ────────────────────────────────────────────────────

  /** Get a terrain group by ID. */
  getTerrain(id) { return this._terrainGroups.get(id); }

  /** Get all terrain group IDs. */
  getTerrainIds() { return [...this._terrainGroups.keys()]; }

  /** Get a wall set by ID. */
  getWallSet(id) { return this._wallSets.get(id); }

  /** Get a floor type by ID. */
  getFloorType(id) { return this._floorTypes.get(id); }

  /**
   * Get floor types valid for a zone.
   * Only returns floors explicitly assigned to this zone.
   * Use getDefaultFloors() for unassigned/generic floors.
   */
  getFloorsForZone(zone) {
    const results = [];
    for (const ft of this._floorTypes.values()) {
      if (ft.zones && ft.zones.includes(zone)) results.push(ft);
    }
    // Fallback: if nothing matches, include floors with no zone restriction
    if (results.length === 0) {
      for (const ft of this._floorTypes.values()) {
        if (!ft.zones || ft.zones.length === 0) results.push(ft);
      }
    }
    return results;
  }

  /** Get floor types with no zone restriction (generic/fallback). */
  getDefaultFloors() {
    return [...this._floorTypes.values()].filter(ft => !ft.zones || ft.zones.length === 0);
  }

  /** Get a multi-tile object def by ID. */
  getObject(id) { return this._objects.get(id); }

  /** Get objects valid for a zone. */
  getObjectsForZone(zone) {
    const results = [];
    for (const obj of this._objects.values()) {
      if (obj.placement?.zones?.includes(zone)) results.push(obj);
    }
    return results;
  }

  /** Get single decorations valid for a zone. */
  getDecorationsForZone(zone, indoor = true) {
    const results = [];
    for (const dec of this._decorations.values()) {
      if (!dec.zones?.includes(zone)) continue;
      if (indoor && dec.outdoor && !dec.indoor) continue;
      if (!indoor && dec.indoor && !dec.outdoor) continue;
      results.push(dec);
    }
    return results;
  }

  /** Get an opening def by ID. */
  getOpening(id) { return this._openings.get(id); }

  /** Get all openings of a given type ('door', 'window', 'archway'). */
  getOpeningsByType(type) {
    const results = [];
    for (const op of this._openings.values()) {
      if (op.type === type) results.push(op);
    }
    return results;
  }

  /** Get all openings. */
  getAllOpenings() { return [...this._openings.values()]; }

  /** Get the role of a tile. */
  getRole(tileId) { return this._tileRoles.get(tileId) || null; }

  /** Get tags for a tile. */
  getTags(tileId) { return this._tileTags.get(tileId) || new Set(); }

  /** Check if a tile has a specific tag. */
  hasTag(tileId, tag) { return this.getTags(tileId).has(tag); }

  /** Which terrain group does this tile belong to? */
  getTerrainForTile(tileId) { return this._tileToTerrain.get(tileId) || null; }

  /** Which object does this tile belong to? */
  getObjectForTile(tileId) { return this._tileToObject.get(tileId) || null; }

  /**
   * Get the target TMX layer for a tile.
   * Priority: explicit tileProperties layer → structure layer → role default → 'objects'.
   */
  getLayerForTile(tileId) {
    // Explicit layer (from tagger tileProperties or structure `layer` field)
    const explicit = this._tileLayers.get(tileId);
    if (explicit) return explicit;
    // Infer from role
    const role = this._tileRoles.get(tileId);
    if (role) return ROLE_DEFAULT_LAYER[role] || LAYER.OBJECTS;
    return LAYER.OBJECTS;
  }

  /**
   * Build a complete tileId → layer mapping for all known tiles.
   * Returns a plain object { [tileId]: 'ground' | 'objects' | 'overhead' }.
   */
  buildLayerMap() {
    const map = {};
    for (const tileId of this._tileRoles.keys()) {
      map[tileId] = this.getLayerForTile(tileId);
    }
    return map;
  }

  // ─── Resolution (delegates to AutotileResolver) ────────────────

  /** Resolve terrain + bitmask to correct tile ID. */
  resolveAutotile(terrainId, bitmask) { return this._resolver.resolveAutotile(terrainId, bitmask); }

  /** Pick random variant of a terrain center tile. */
  resolveVariant(terrainId, rngFn) { return this._resolver.resolveVariant(terrainId, rngFn); }

  /** Resolve wall set + position to tile ID. */
  resolveWall(wallSetId, position) { return this._resolver.resolveWall(wallSetId, position); }

  /** Reduce 8-bit blob bitmask to 4-bit cardinal (public for tests). */
  _reduce8to4(bitmask8) { return this._resolver.reduce8to4(bitmask8); }

  // ─── Zone Mapping (delegates to ZoneMapper) ───────────────────

  /** Build zone-to-tile mapping for generators. */
  buildZoneMapping(zoneConfig = {}) { return this._zoneMapper.build(zoneConfig); }

  /** Get all wall set IDs. */
  getWallSetIds() { return [...this._wallSets.keys()]; }

  // ─── Serialization ────────────────────────────────────────────

  /** Export to JSON for saving alongside tileset. */
  toJSON() {
    return {
      schemaVersion: SCHEMA_VERSION,
      id: this.id,
      name: this.name,
      tileSize: this.tileSize,
      columns: this.columns,
      tileCount: this.tileCount,
      attribution: this.attribution,
      terrainGroups: [...this._terrainGroups.values()],
      wallSets: [...this._wallSets.values()],
      floorTypes: [...this._floorTypes.values()],
      objects: [...this._objects.values()],
      decorations: [...this._decorations.values()],
      openings: [...this._openings.values()],
      tileRoles: Object.fromEntries(this._tileRoles),
      tileLayers: this._tileLayers.size > 0 ? Object.fromEntries(this._tileLayers) : undefined,
      tileTags: Object.fromEntries(
        [...this._tileTags.entries()].map(([id, tags]) => [id, [...tags]])
      ),
      tileHashes: this._tileHashes.size > 0 ? Object.fromEntries(this._tileHashes) : undefined,
    };
  }

  /** Load from JSON. */
  static fromJSON(json) {
    return new TileMeta(typeof json === 'string' ? JSON.parse(json) : json);
  }

  // ─── Stats / Debug ────────────────────────────────────────────

  /** Summary of what this TileMeta knows. */
  getSummary() {
    return {
      id: this.id,
      name: this.name,
      attribution: this.attribution,
      tileCount: this.tileCount,
      classified: this._tileRoles.size,
      unclassified: this.tileCount - this._tileRoles.size,
      terrainGroups: this._terrainGroups.size,
      wallSets: this._wallSets.size,
      floorTypes: this._floorTypes.size,
      objects: this._objects.size,
      decorations: this._decorations.size,
      openings: this._openings.size,
      taggedTiles: this._tileTags.size,
    };
  }

  /**
   * Get purchase/attribution info for display when tileset image is missing.
   * Returns null if no attribution is set.
   * @returns {{ artist: string, purchaseUrl: string, message: string } | null}
   */
  getMissingTilesetInfo() {
    if (!this.attribution) return null;
    const a = this.attribution;
    return {
      artist: a.artist || 'Unknown artist',
      purchaseUrl: a.purchaseUrl || null,
      website: a.website || null,
      licenseType: a.licenseType || null,
      message: a.purchaseUrl
        ? `This map uses "${this.name}" by ${a.artist || 'the original artist'}. Purchase it to edit this map.`
        : `This map uses "${this.name}". Contact the artist to obtain this tileset.`,
    };
  }

  // ─── Tile Hashing ──────────────────────────────────────────────

  /** Get the stored content hash for a tile. */
  getTileHash(tileId) { return this._tileHashes.get(tileId) || null; }

  /** Store tile content hashes (from TileHasher.hashAllTiles()). */
  setTileHashes(hashMap) {
    if (hashMap instanceof Map) {
      for (const [id, hash] of hashMap) this._tileHashes.set(id, hash);
    } else {
      for (const [id, hash] of Object.entries(hashMap)) this._tileHashes.set(Number(id), hash);
    }
  }

  /** Check if tile hashes are stored. */
  hasTileHashes() { return this._tileHashes.size > 0; }

  // ─── Visualizer Data ───────────────────────────────────────────

  /**
   * Get structured data for visualizing what this TileMeta knows.
   * Pure data — no DOM or rendering. A UI component consumes this.
   */
  getVisualizerData() {
    return {
      terrainGroups: this._vizTerrains(),
      wallSets: this._vizWallSets(),
      floorTypes: this._vizFloorTypes(),
      objects: this._vizObjects(),
      decorations: [...this._decorations.values()],
      openings: [...this._openings.values()],
      coverage: this._vizCoverage(),
    };
  }

  _vizTerrains() {
    const BITMASK_LABELS_4 = { 0:'center', 1:'N', 2:'E', 3:'NE', 4:'S', 5:'NS', 6:'SE', 7:'NES', 8:'W', 9:'NW', 10:'EW', 11:'NEW', 12:'SW', 13:'NSW', 14:'SEW', 15:'all' };
    const result = [];
    for (const [id, tg] of this._terrainGroups) {
      const expected = tg.autotileFormat === '47blob' ? 47 : tg.autotileFormat === 'road' ? 15 : tg.autotileFormat === '4bit' ? 16 : 0;
      const variantCount = Object.keys(tg.variants || {}).length;
      const variants = Object.entries(tg.variants || {}).map(([mask, tileId]) => ({
        bitmask: Number(mask),
        tileId,
        label: BITMASK_LABELS_4[mask] || `mask_${mask}`,
      }));
      result.push({
        id, format: tg.autotileFormat, centerTile: tg.centerTile,
        variantCount, expectedVariants: expected,
        completeness: expected > 0 ? Math.round((variantCount / expected) * 100) : 100,
        variants, randomVariants: tg.randomVariants || [], transitionsTo: tg.transitionsTo || [],
      });
    }
    return result;
  }

  _vizWallSets() {
    const ALL_POSITIONS = ['top','bottom','left','right','cornerTL','cornerTR','cornerBL','cornerBR','innerTL','innerTR','innerBL','innerBR','teeN','teeE','teeS','teeW','cross','endN','endE','endS','endW','single'];
    const result = [];
    for (const [id, ws] of this._wallSets) {
      const tiles = ALL_POSITIONS.map(pos => ({ position: pos, tileId: ws[pos] ?? null, defined: ws[pos] != null }));
      const defined = tiles.filter(t => t.defined).length;
      result.push({ id, tiles, defined, total: ALL_POSITIONS.length, completeness: Math.round((defined / ALL_POSITIONS.length) * 100) });
    }
    return result;
  }

  _vizFloorTypes() {
    return [...this._floorTypes.values()].map(ft => ({
      id: ft.id, tile: ft.tile, variants: ft.variants || [], zones: ft.zones || [],
    }));
  }

  _vizObjects() {
    return [...this._objects.values()].map(obj => ({
      id: obj.id, tiles: obj.tiles, footprint: obj.footprint,
      placement: obj.placement || {}, blocking: obj.blocking, clippable: obj.clippable,
    }));
  }

  _vizCoverage() {
    const byRole = {};
    for (const [, role] of this._tileRoles) {
      byRole[role] = (byRole[role] || 0) + 1;
    }
    // Find contiguous unclassified ranges
    const unclassifiedRanges = [];
    let rangeStart = null;
    for (let i = 0; i <= this.tileCount; i++) {
      const classified = this._tileRoles.has(i);
      if (!classified && i < this.tileCount) {
        if (rangeStart === null) rangeStart = i;
      } else {
        if (rangeStart !== null) {
          unclassifiedRanges.push({ start: rangeStart, end: i - 1 });
          rangeStart = null;
        }
      }
    }
    return {
      totalTiles: this.tileCount, classified: this._tileRoles.size,
      unclassified: this.tileCount - this._tileRoles.size, byRole, unclassifiedRanges,
    };
  }
}


// ─── Road Shape Constants ────────────────────────────────────────────

export const ROAD_SHAPES = {
  DEAD_N:     1,     // dead-end open to north
  DEAD_E:     2,
  DEAD_S:     4,
  DEAD_W:     8,
  STRAIGHT_V: 5,     // N+S
  STRAIGHT_H: 10,    // E+W
  CURVE_NE:   3,     // N+E
  CURVE_SE:   6,     // S+E
  CURVE_SW:   12,    // S+W
  CURVE_NW:   9,     // N+W
  TEE_N:      11,    // E+W+N (no south)
  TEE_E:      7,     // N+S+E (no west)
  TEE_S:      14,    // E+W+S (no north)
  TEE_W:      13,    // N+S+W (no east)
  CROSS:      15,    // all four
};


// ─── 47-Blob Canonical Bitmask Values ────────────────────────────────

/**
 * The 47 unique shapes produced by 8-bit blob autotiling.
 * Diagonal bits only matter when both adjacent cardinals match.
 *
 * Bit positions: NW=1 N=2 NE=4 W=8 E=16 SW=32 S=64 SE=128
 *
 * These are the canonical bitmask values after masking irrelevant diagonals.
 * Artists typically arrange their 47-tile blob sets in a standard order;
 * this array maps index (0–46) to bitmask for that order.
 */
/**
 * Self-check: verify the 47 masks are unique and cover the expected count.
 * Runs once on module load — zero runtime cost after that.
 */
function _verifyBlobMasks(masks) {
  const unique = new Set(masks);
  if (unique.size !== 47) {
    console.error(`BLOB_47_MASKS has ${unique.size} unique values, expected 47`);
  }
  if (masks.length !== 47) {
    console.error(`BLOB_47_MASKS has ${masks.length} entries, expected 47`);
  }
  // Verify all values are in 0-255 range
  for (const m of masks) {
    if (m < 0 || m > 255 || !Number.isInteger(m)) {
      console.error(`BLOB_47_MASKS contains invalid value: ${m}`);
    }
  }
}

export const BLOB_47_MASKS = [
  0,    // isolated
  2,    // N only
  8,    // W only
  10,   // N+W
  11,   // NW+N+W
  16,   // E only
  18,   // N+E
  22,   // N+NE+E
  24,   // W+E
  26,   // N+W+E
  27,   // NW+N+W+E
  30,   // N+NE+W+E
  31,   // all north row
  64,   // S only
  66,   // N+S
  72,   // W+S
  74,   // N+W+S
  75,   // NW+N+W+S
  80,   // E+S
  82,   // N+E+S
  86,   // N+NE+E+S
  88,   // W+E+S
  90,   // N+W+E+S
  91,   // NW+N+W+E+S
  94,   // N+NE+W+E+S
  95,   // NW+N+NE+W+E+S
  104,  // W+SW+S
  106,  // N+W+SW+S
  107,  // NW+N+W+SW+S
  120,  // W+SW+E+S
  122,  // N+W+SW+E+S (no NW, no NE, no SE)
  123,  // NW+N+W+SW+E+S
  126,  // N+NE+W+SW+E+S
  127,  // NW+N+NE+W+SW+E+S
  208,  // E+S+SE
  210,  // N+E+S+SE
  214,  // N+NE+E+S+SE
  216,  // W+E+S+SE
  218,  // N+W+E+S+SE
  219,  // NW+N+W+E+S+SE
  222,  // N+NE+W+E+S+SE
  223,  // NW+N+NE+W+E+S+SE
  248,  // W+SW+E+S+SE
  250,  // N+W+SW+E+S+SE
  251,  // NW+N+W+SW+E+S+SE
  254,  // N+NE+W+SW+E+S+SE
  255,  // all 8 neighbors
];

_verifyBlobMasks(BLOB_47_MASKS);
